/**
 * SHEIN 发品表单提交前校验（对齐 publishOrEdit 常用必填与格式）。
 * 图片只需已上传（有可访问地址）；SHEIN 转图由后端自动完成。
 * @returns {{ ok: true } | { ok: false, message: string, section?: string }}
 */

import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { validateLinkedAttributeRules } from '@/shein-platform/utils/sheinAssociatedAttributeRules';
import {
  attributeDisplayLabel,
  isBlankSheinAttributeValueId,
  resolveSkcColorAgainstTemplate,
} from '@/shein-platform/utils/sheinAttributeTemplate';
import {
  collectSheinEffectiveRequiredAttrs,
  collectSheinSiblingGroupedIds,
  collectSheinSiblingSatisfiedIds,
  isSheinAttrFilled,
  splitSheinRequiredAttrs,
} from '@/shein-platform/utils/sheinRequiredAttrGroups';
import { EMPTY_SKU_PACKAGE_CAPS } from '@/shein-platform/utils/sheinPublishFillInStandard';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import { validateSupplierSkusLocal } from '@/shein-platform/utils/sheinPublishPreflight';
import { validateSizeChartForm } from '@/shein-platform/utils/sheinSizeChart';

function imageUrlOf(image) {
  if (!image) return '';
  return String(
    image.sheinUrl
    || image.publicUrl
    || image.previewUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
}

function isPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

function isNonNegativeInteger(value) {
  const text = String(value ?? '').trim();
  if (!/^\d+$/.test(text)) return false;
  return Number(text) >= 0;
}

function isValidHopeOnSaleDate(value) {
  return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(String(value || '').trim());
}

function fail(message, section, field = 'form') {
  return { ok: false, message: fixedUi(message), section, field };
}

/**
 * @param {object} form
 * @param {object} ctx
 * @param {boolean} [ctx.isEdit]
 * @param {string|number} [ctx.shopId]
 * @param {object|null} [ctx.attrTpl]
 * @param {object|null} [ctx.colorAttr]
 * @param {object|null} [ctx.sizeAttr]
 * @param {Array} [ctx.sizeMeasureAttrs]
 * @param {Array} [ctx.warehouseOptions]
 * @param {{ quantityInfo?: { show?: boolean, required?: boolean }, packageType?: { show?: boolean, required?: boolean } }} [ctx.skuPkgCaps]
 * @param {string} [ctx.shopType]
 * @param {Map|object|null} [ctx.linkedAttrRules]
 */
export function validateSheinPublishForm(form = {}, ctx = {}) {
  const {
    isEdit = false,
    shopId,
    shopType = '',
    attrTpl = null,
    colorAttr = null,
    sizeAttr = null,
    sizeMeasureAttrs = [],
    warehouseOptions = [],
    skuPkgCaps = EMPTY_SKU_PACKAGE_CAPS,
    linkedAttrRules = null,
  } = ctx;
  const quantityCap = {
    show: skuPkgCaps?.quantityInfo?.show === true,
    required: skuPkgCaps?.quantityInfo?.required === true,
  };
  const packageCap = {
    show: skuPkgCaps?.packageType?.show === true,
    required: skuPkgCaps?.packageType?.required === true,
  };

  if (!shopId) return fail('请先选择店铺', 'basic', 'form');

  const title = String(form.title || '').trim();
  if (!title) return fail('请填写产品标题', 'basic', 'title');
  if (title.length < 2) return fail('产品标题至少 2 个字符', 'basic', 'title');
  if (title.length > 150) return fail('产品标题不能超过 150 个字符', 'basic', 'title');
  if (/[\u3400-\u9fff]/.test(title)) {
    return fail('产品标题不能包含中文，请改为英文后再推送', 'basic', 'title');
  }
  if (!form.titleLanguage) return fail('请选择标题语言', 'basic', 'titleLanguage');

  if (!form.categoryId) return fail('请选择商品类目', 'basic', 'categoryId');
  if (!form.productTypeId) return fail('当前类目缺少商品类型，请重新选择末级类目', 'basic', 'categoryId');

  const supplierCode = String(form.supplierCode || '').trim();
  if (!supplierCode) return fail('请填写商家货号', 'basic', 'supplierCode');
  // 允许字母数字下划线短横线加号与常用中文；填写时以 - 结尾，保存/带入印花名后为「前缀-+印花名」
  if (!/^[A-Za-z0-9_\u4e00-\u9fff+-]{1,100}$/.test(supplierCode)) {
    return fail('商家货号仅支持字母、数字、中文、下划线、短横线、加号，最长 100 位', 'basic', 'supplierCode');
  }
  if (!supplierCode.includes('-')) {
    return fail('商家货号格式要求：须含短横线「-」（填写时以 - 结尾，保存时自动拼接印花图名称）', 'basic', 'supplierCode');
  }

  if (!isEdit && !(form.selectedSites || []).length) {
    return fail('请选择上架站点', 'sales', 'selectedSites');
  }

  if (!String(form.currency || '').trim()) {
    const selfOperated = sheinShopCapabilities(shopType || form.shopType).usesPriceInfo;
    return fail(
      selfOperated
        ? '缺少售价币种：请等待店铺站点列表币种加载完成'
        : '缺少供货价币种：请等待「商品发布规范」币种加载完成',
      'sales',
      'currency',
    );
  }

  const shelfWay = String(form.shelfWay || '1');
  if (shelfWay !== '1' && shelfWay !== '2') {
    return fail('上架方式无效，请选择自动上架或定时上架', 'sales');
  }
  if (shelfWay === '2') {
    const hope = String(form.hopeOnSaleDate || '').trim();
    if (!hope) return fail('定时上架请选择期望上架时间', 'sales');
    if (!isValidHopeOnSaleDate(hope)) {
      return fail('期望上架时间格式须为 yyyy-MM-dd HH:mm:ss', 'sales');
    }
  }

  // 非服装类目常见：建议零售价 / 最小备货数量（有值才校验，是否必填由发布规范决定）
  const suggestRaw = String(form.suggestedRetailPrice ?? '').trim();
  if (suggestRaw) {
    if (!/^\d+(\.\d{1,2})?$/.test(suggestRaw) || !isPositiveNumber(suggestRaw)) {
      return fail('建议零售价须为大于 0 的数字，最多 2 位小数', 'sales', 'suggestedRetailPrice');
    }
  }
  const minStockRaw = String(form.minimumStockQuantity ?? '').trim();
  if (minStockRaw && !/^\d+$/.test(minStockRaw)) {
    return fail('最小备货数量须为正整数', 'sales', 'minimumStockQuantity');
  }

  // 多仓：stock_info_list.supplier_warehouse_id 必填，需明确库存归属仓库
  if (Array.isArray(warehouseOptions) && warehouseOptions.length > 1 && !String(form.warehouseId || '').trim()) {
    return fail('当前店铺为多仓，请选择库存所在仓库', 'sales', 'warehouseId');
  }

  const skcLabel = attributeDisplayLabel(colorAttr) || fixedUi("主规格");
  const skuSpecLabel = attributeDisplayLabel(sizeAttr) || fixedUi('其他规格');

  const skcList = Array.isArray(form.skcList) ? form.skcList : [];
  const coloredSkcs = skcList.filter((skc) => (
    skc?.colorValueId
    || String(skc?.colorExtraValue || '').trim()
    || String(skc?.colorLabel || '').trim()
  ));
  if (!coloredSkcs.length) {
    return fail(colorAttr ? fixedUiTpl('请选择或自定义{0}', skcLabel) : fixedUiTpl('请添加{0}', skcLabel), 'spec', 'skcList');
  }

  if (sizeAttr?.values?.length && !(form.selectedSizeIds || []).length) {
    return fail(fixedUiTpl('请选择{0}', skuSpecLabel), 'spec', 'skcList');
  }

  for (const skc of coloredSkcs) {
    const resolved = resolveSkcColorAgainstTemplate(skc, colorAttr);
    const colorName = resolved.colorLabel || resolved.colorExtraValue || skc.colorLabel || skc.colorExtraValue || skcLabel;
    const colorAttrId = String(resolved.colorAttributeId || colorAttr?.attributeId || '').trim();
    if (colorAttr && !colorAttrId) {
      return fail(fixedUiTpl('「{0}」缺少{1}属性，请刷新类目属性模板后重试', colorName, skcLabel), 'spec', 'skcList');
    }
    if (colorAttr && isBlankSheinAttributeValueId(resolved.colorValueId)) {
      const customLabel = String(resolved.colorExtraValue || resolved.colorLabel || '').trim();
      if (!customLabel) {
        return fail(
          fixedUiTpl('「{0}」的{1}未匹配到平台属性值，请在模板中重新选择颜色', colorName, skcLabel),
          'spec',
          'skcList',
        );
      }
    }
    if (!imageUrlOf(skc.mainImage)) {
      return fail(fixedUiTpl('请为「{0}」上传主图（需符合平台图片规则）', colorName), 'spec', 'skcList');
    }
    const details = Array.isArray(skc.detailImages) ? skc.detailImages : [];
    if (details.length > 10) {
      return fail(fixedUiTpl('「{0}」细节图最多 10 张', colorName), 'spec', 'skcList');
    }
    for (let i = 0; i < details.length; i += 1) {
      if (!imageUrlOf(details[i])) {
        return fail(fixedUiTpl('「{0}」第 {1} 张细节图无效，请重新上传', colorName, i + 1), 'spec', 'skcList');
      }
    }

    const skuRows = Array.isArray(skc.skuRows) ? skc.skuRows : [];
    if (!skuRows.length) {
      return fail(fixedUiTpl('「{0}」缺少 SKU 供应信息，请选择{1}或检查规格', colorName, skuSpecLabel), 'spec', 'supply');
    }

    for (const sku of skuRows) {
      const sizeLabel = sku.sizeValueId || 'SKU';
      const price = sku.price ?? form.supplyPrice;
      if (price === '' || price == null) {
        return fail(fixedUiTpl('请填写「{0} / {1}」供货价', colorName, sizeLabel), 'spec', 'supply');
      }
      if (!isPositiveNumber(price)) {
        return fail(fixedUiTpl('「{0} / {1}」供货价须为大于 0 的数字', colorName, sizeLabel), 'spec', 'supply');
      }

      const stock = sku.stock === '' || sku.stock == null ? form.stockQty : sku.stock;
      if (stock === '' || stock == null) {
        return fail(fixedUiTpl('请填写「{0} / {1}」库存', colorName, sizeLabel), 'spec', 'supply');
      }
      if (!isNonNegativeInteger(stock)) {
        return fail(fixedUiTpl('「{0} / {1}」库存须为 ≥0 的整数', colorName, sizeLabel), 'spec', 'supply');
      }

      const weight = sku.weight ?? form.weightGrams;
      if (weight === '' || weight == null) {
        return fail(fixedUiTpl('请填写「{0} / {1}」包装重量', colorName, sizeLabel), 'size', 'supply');
      }
      if (!isPositiveNumber(weight)) {
        return fail(fixedUiTpl('「{0} / {1}」包装重量须为大于 0 的数字', colorName, sizeLabel), 'size', 'supply');
      }

      const length = sku.length ?? form.pkgLength;
      const width = sku.width ?? form.pkgWidth;
      const height = sku.height ?? form.pkgHeight;
      if (length === '' || length == null || width === '' || width == null || height === '' || height == null) {
        return fail(fixedUiTpl('请填写「{0} / {1}」包装尺寸（长×宽×高）', colorName, sizeLabel), 'size', 'supply');
      }
      if (!isPositiveNumber(length) || !isPositiveNumber(width) || !isPositiveNumber(height)) {
        return fail(fixedUiTpl('「{0} / {1}」包装尺寸须为大于 0 的数字', colorName, sizeLabel), 'size', 'supply');
      }

      if (quantityCap.show) {
        const qtyRaw = String(sku.quantity ?? '').trim();
        if (quantityCap.required && !qtyRaw) {
          return fail(fixedUiTpl('请填写「{0} / {1}」包装件数', colorName, sizeLabel), 'size', 'supply');
        }
        if (qtyRaw) {
          const qty = Number(qtyRaw);
          if (!Number.isInteger(qty) || qty <= 0) {
            return fail(fixedUiTpl('「{0} / {1}」包装件数须为正整数', colorName, sizeLabel), 'size', 'supply');
          }
          const unit = Number(sku.quantityUnit || 1);
          if (![1, 2, 3].includes(unit)) {
            return fail(fixedUiTpl('「{0} / {1}」请选择包装单位（件/双/套）', colorName, sizeLabel), 'size', 'supply');
          }
        }
      }

      if (packageCap.show) {
        const pkgType = sku.packageType;
        const hasPkg = pkgType !== '' && pkgType != null && String(pkgType) !== '0';
        if (packageCap.required && !hasPkg) {
          return fail(fixedUiTpl('请选择「{0} / {1}」包装类型', colorName, sizeLabel), 'size', 'supply');
        }
        if (hasPkg) {
          const pt = Number(pkgType);
          if (![1, 2, 3, 4].includes(pt)) {
            return fail(fixedUiTpl('「{0} / {1}」包装类型无效', colorName, sizeLabel), 'size', 'supply');
          }
        }
      }

      if (sizeAttr?.values?.length && !sku.sizeValueId) {
        return fail(fixedUiTpl('「{0}」SKU 缺少{1}，请重新勾选{2}', colorName, skuSpecLabel, skuSpecLabel), 'spec', 'skcList');
      }
    }

    // 同 SKC 下件数单位须一致；若任一行填了件数，则该 SKC 全部 SKU 都须填写（仅规范允许时）
    if (quantityCap.show) {
      const qtyFilled = skuRows.filter((sku) => String(sku.quantity ?? '').trim());
      if (qtyFilled.length) {
        if (qtyFilled.length !== skuRows.length) {
          return fail(
            fixedUiTpl('「{0}」已填写部分 SKU 包装件数，同规格下全部 SKU 都须填写件数', colorName),
            'size',
            'supply',
          );
        }
        const units = new Set(
          skuRows.map((sku) => String(sku.quantityUnit || '1')),
        );
        if (units.size > 1) {
          return fail(
            fixedUiTpl('「{0}」同规格下包装单位须保持一致（件/双/套）', colorName),
            'size',
            'supply',
          );
        }
      }
    }

    // partialEdit：同 SKC 下任一 SKU 有 SKU 图时，其余 SKU 也须有图（平台会整体校验）
    if (isEdit) {
      const withImg = skuRows.filter((sku) => imageUrlOf(sku?.skuImage));
      if (withImg.length && withImg.length !== skuRows.length) {
        return fail(
          fixedUiTpl('「{0}」已为部分 SKU 上传 SKU 图，部分编辑要求同规格下全部 SKU 都提交 SKU 图', colorName),
          'spec',
          'skcList',
        );
      }
    }
  }

  const skuUniqueLocal = validateSupplierSkusLocal(form);
  if (!skuUniqueLocal.ok) return skuUniqueLocal;

  // 同名必填属性（如官方模板里两个「主料克重」）：平台只要求填一项，
  // 同组内已填任一项时，其余项不再逐项要求，只对已填值做格式校验。
  const effectiveRequiredAttrs = collectSheinEffectiveRequiredAttrs(attrTpl, linkedAttrRules);
  const siblingSatisfiedIds = collectSheinSiblingSatisfiedIds(
    effectiveRequiredAttrs,
    form.productAttributes,
  );
  const siblingGroupedIds = collectSheinSiblingGroupedIds(effectiveRequiredAttrs);
  const { groups: requiredGroups } = splitSheinRequiredAttrs(
    attrTpl?.requiredProduct || [],
    form.productAttributes,
  );

  for (const attr of (attrTpl?.requiredProduct || [])) {
    const val = form.productAttributes?.[attr.attributeId];
    const attrId = String(attr.attributeId ?? '').trim();
    // 同名组（如同现两个「主料克重」）不逐项要求：未填时交给组校验「至少填一项」，
    // 已填时仍按原规则校验格式。
    if (siblingGroupedIds.has(attrId) && !isSheinAttrFilled(attr, form.productAttributes)) {
      continue;
    }
    if (attr.mode === 4 || attr.isComposition) {
      const rows = Array.isArray(val) ? val : [];
      if (!rows.length) return fail(fixedUiTpl('请填写必填属性：{0}', attr.name), 'attrs', 'productAttributes');
      for (const row of rows) {
        if (!row?.valueId) return fail(fixedUiTpl('{0}：请选择成分项', attr.name), 'attrs', 'productAttributes');
        if (!isPositiveNumber(row.percent)) return fail(fixedUiTpl('{0}：比例须为大于 0 的数字', attr.name), 'attrs', 'productAttributes');
      }
      const total = rows.reduce((s, r) => s + (Number(r.percent) || 0), 0);
      if (Math.round(total) !== 100) return fail(fixedUiTpl('{0} 比例合计须为 100%', attr.name), 'attrs', 'productAttributes');
    } else if (attr.mode === 1) {
      if (!Array.isArray(val) || !val.length) return fail(fixedUiTpl('请选择必填属性：{0}', attr.name), 'attrs', 'productAttributes');
    } else if (val == null || String(val).trim() === '') {
      return fail(fixedUiTpl('请填写必填属性：{0}', attr.name), 'attrs', 'productAttributes');
    }
  }

  // 同名组内一项都没填：提示「至少填一项」，而不是逐个报必填
  if (requiredGroups.length > 0) {
    return fail(fixedUiTpl('请至少填写必填属性：{0}', requiredGroups[0].label), 'attrs', 'productAttributes');
  }

  const linkedCheck = validateLinkedAttributeRules(form, linkedAttrRules, attrTpl, {
    satisfiedSiblingIds: siblingSatisfiedIds,
  });
  if (!linkedCheck.ok) return linkedCheck;

  if (sizeMeasureAttrs.length) {
    const sizeCheck = validateSizeChartForm({
      sizeChart: form.sizeChart,
      measureAttrs: sizeMeasureAttrs,
      sizeAttr,
      selectedSizeIds: form.selectedSizeIds || [],
      skcList: form.skcList || [],
    });
    if (!sizeCheck.ok) {
      return fail(sizeCheck.message, sizeCheck.section || 'size', sizeCheck.field || 'sizeChart');
    }
  }

  return { ok: true };
}
